import './InlineVideo.sass';

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { InlineVideoProps } from './InlineVideo.interface';
import { mainActions, mainSelector } from '$store/main';
import { useVisibleElement } from '$hooks';
import { IcPlay28, IcVideo28 } from '$assets';
import { useVideoPosterUrl, useVideoUrl } from '$hooks/useMediaUrl';
import { useThumbHash } from '$hooks';
import { classNames, getFormattedTime } from '$utils';
import { cancelPreloadMedia, preloadMedia } from '../../../services/mediaLoader';

export const InlineVideo: FC<InlineVideoProps> = (props) => {
  const {
    parentObject,
    video,
    decryptKey,
    isHidden,
    maxWidth: maxWidthPassed = 600,
    isBlocked,
    isVisible = false,
  } = props;

  const ref = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch();
  const { playingVideo } = useSelector(mainSelector);
  const [isPlaying, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const elem = useRef(null);
  const { visible } = useVisibleElement(elem);
  const [maxWidth, setMaxWidth] = useState(maxWidthPassed);

  useEffect(() => {
    if (ref.current) {
      const newMaxWidth = Math.min(maxWidthPassed, ref.current!.offsetWidth);
      setMaxWidth(newMaxWidth);
    }
  }, []);

  useEffect(() => {
    if (isVisible) {
      if (!isPlaying && !isHidden && !isBlocked) {
        setPlaying(true);
        dispatch(mainActions.setPlayingVideo(parentObject));
      }
    } else {
      if (isPlaying) {
        setPlaying(false);
      }
    }
  }, [isVisible]);

  useEffect(() => {
    if (!visible && isPlaying) {
      setPlaying(false);
    }
  }, [visible]);

  useEffect(() => {
    if (isPlaying && playingVideo !== parentObject) {
      setPlaying(false);
    }
  }, [playingVideo]);

  const handlePlay = () => {
    if (isHidden || isBlocked) {
      return;
    }

    setPlaying(true);
    dispatch(mainActions.setPlayingVideo(parentObject));
  };

  const src = useVideoUrl(video, decryptKey);
  const rawPosterSrc = useVideoPosterUrl(video, decryptKey);
  const placeholder = useThumbHash(video.thumbHash, video.thumbnail);
  const posterSrc = rawPosterSrc || placeholder;

  useEffect(() => {
    if (isHidden || isBlocked || isVisible) {
      return;
    }
    preloadMedia(src, 'video');
    return () => cancelPreloadMedia(src);
  }, [src]);

  const size = useMemo(() => {
    let width: number, height: number;
    if (video.height > video.width) {
      height = Math.min(500, video.height);
      width = (video.width / video.height) * height;
    } else {
      width = Math.min(maxWidth, video.width);
      height = (video.height / video.width) * width;
    }

    return {
      width: Math.max(width, 220),
      height: Math.max(height, 220),
    };
  }, [maxWidth]);

  const handleClick = useCallback(
    (e) => {
      if (muted) {
        setMuted(false);
      }
      !isBlocked && e.stopPropagation();
    },
    [isBlocked],
  );

  const handleUnmute = useCallback(
    (e) => {
      setMuted(false);
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
      }
      !isBlocked && e.stopPropagation();
    },
    [isBlocked, videoRef],
  );

  return (
    <div ref={ref} className="InlineVideo__wrap">
      <div className="InlineVideo__bg" style={{ backgroundImage: placeholder ? `url(${placeholder})` : undefined }} />
      <div
        className="InlineVideo"
        onClick={handleClick}
        ref={elem}
        style={{ width: size.width, height: size.height }}
      >
        {!isPlaying && (
          <>
            <div
              className={classNames({
                InlineVideo__poster: true,
                blur: isBlocked,
              })}
              style={{
                backgroundImage: posterSrc
                  ? `linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(${posterSrc})`
                  : 'linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
              }}
              onClick={handlePlay}
            />
            <div className="InlineVideo__info" onClick={handlePlay}>
              {!isBlocked ? (
                <div className="ExternalVideo__play_btn">
                  <IcPlay28 />
                </div>
              ) : (
                <div className="Collage__mediaIcon">
                  <IcVideo28 />
                </div>
              )}
            </div>
            {!!video.duration && (
              <div className="ExternalVideo__duration_label">
                {getFormattedTime(video.duration)}
              </div>
            )}
          </>
        )}
        {isPlaying && muted && (
          <div className="ExternalVideo__unmute_helper" onClick={handleUnmute} />
        )}
        {isPlaying && (
          <video
            autoPlay
            poster={posterSrc || undefined}
            controls
            muted={muted}
            playsInline
            preload="auto"
            loop={video.duration ? video.duration < 10 : false}
            ref={videoRef}
            src={src}
          />
        )}
      </div>
    </div>
  );
};
